import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ExternalLink, Loader2, AlertCircle, ShieldCheck, FileText, Image as ImageIcon } from 'lucide-react';
import { previewEvidence, fetchEvidenceInlineBlob, downloadEvidence } from '@/services/grc';

const severityBadge = (sev) => {
  const s = (sev || '').toLowerCase();
  if (s === 'critical') return 'bg-pink-100 text-pink-800 border-pink-300';
  if (s === 'high') return 'bg-orange-100 text-orange-800 border-orange-300';
  if (s === 'medium') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  if (s === 'low') return 'bg-slate-100 text-slate-700 border-slate-300';
  return 'bg-gray-100 text-gray-700 border-gray-300';
};

const EvidencePreviewModal = ({ open, onOpenChange, evidence }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [inlineUrl, setInlineUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !evidence?.id) return;
    setLoading(true);
    setData(null);
    setInlineUrl(null);
    setError(null);
    let revokedUrl = null;
    (async () => {
      try {
        const meta = await previewEvidence(evidence.id);
        setData(meta);
        if (meta.kind === 'file' && meta.file_type && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'pdf'].includes((meta.file_type || '').toLowerCase())) {
          // Fetch inline blob for image/PDF rendering
          const { blob } = await fetchEvidenceInlineBlob(evidence.id);
          revokedUrl = URL.createObjectURL(blob);
          setInlineUrl(revokedUrl);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (revokedUrl) URL.revokeObjectURL(revokedUrl); };
  }, [open, evidence?.id]);

  const handleDownload = async () => {
    try { await downloadEvidence(evidence.id, evidence.file_name || `${evidence.title}.json`); } catch (e) { console.error('download failed', e); }
  };

  if (!evidence) return null;

  const snapshot = data?.snapshot;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="evidence-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-indigo-600" />
            {evidence.title || 'Evidence Preview'}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            ID: <span className="font-mono">{evidence.id}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading preview…
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        ) : data?.kind === 'snapshot' && snapshot ? (
          // ===== Auto-collected CSPM snapshot (auditor-friendly view) =====
          <div className="space-y-4" data-testid="evidence-snapshot-view">
            {/* Severity & Compliance header */}
            <div className="flex flex-wrap gap-2 items-center">
              {snapshot.severity && (
                <Badge variant="outline" className={severityBadge(snapshot.severity)}>
                  {String(snapshot.severity).toUpperCase()}
                </Badge>
              )}
              {snapshot.framework && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {snapshot.framework}
                  {snapshot.control_id ? ` · ${snapshot.control_id}` : ''}
                </Badge>
              )}
              {snapshot.provider && (
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300">
                  {String(snapshot.provider).toUpperCase()}
                </Badge>
              )}
              {snapshot.service && (
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300">
                  {String(snapshot.service).toUpperCase()}
                </Badge>
              )}
            </div>

            {/* Compliance Statement (auditor summary) */}
            <section className="bg-indigo-50 border border-indigo-200 rounded p-4">
              <h4 className="text-xs uppercase font-semibold text-indigo-700 mb-1">Compliance Statement</h4>
              <p className="text-sm text-gray-800">
                On <strong>{new Date(snapshot.collected_at || snapshot.scan_completed_at || Date.now()).toLocaleString()}</strong>,
                an automated CSPM scan against the configured cloud account
                {snapshot.framework ? <> identified a configuration finding mapped to <strong>{snapshot.framework}</strong>{snapshot.control_id ? <> control <strong>{snapshot.control_id}</strong></> : null}.</> : '.'}
                {' '}
                <strong>{snapshot.findings_count || 0}</strong> affected resource{(snapshot.findings_count || 0) === 1 ? '' : 's'} were identified at <strong>{(snapshot.severity || 'unknown').toString().toUpperCase()}</strong> severity.
              </p>
            </section>

            {snapshot.description && (
              <section>
                <h4 className="text-xs uppercase text-gray-500 mb-1">What this control verifies</h4>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{snapshot.description}</p>
              </section>
            )}

            {snapshot.risk && (
              <section>
                <h4 className="text-xs uppercase text-gray-500 mb-1">Risk</h4>
                <p className="text-sm text-amber-700 whitespace-pre-wrap">{snapshot.risk}</p>
              </section>
            )}

            {snapshot.remediation && (
              <section>
                <h4 className="text-xs uppercase text-gray-500 mb-1">Recommended Remediation</h4>
                <p className="text-sm text-emerald-700 whitespace-pre-wrap">{snapshot.remediation}</p>
                {snapshot.remediation_code && (
                  <pre className="mt-2 text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto">{snapshot.remediation_code}</pre>
                )}
              </section>
            )}

            {Array.isArray(snapshot.affected_resources) && snapshot.affected_resources.length > 0 && (
              <section>
                <h4 className="text-xs uppercase text-gray-500 mb-2">Affected Resources ({snapshot.affected_resources.length})</h4>
                <div className="border border-gray-200 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left p-2">Resource ID</th>
                        <th className="text-left p-2">Region</th>
                        <th className="text-left p-2">Severity</th>
                        <th className="text-left p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {snapshot.affected_resources.slice(0, 10).map((r, i) => (
                        <tr key={i}>
                          <td className="p-2 font-mono text-gray-800 truncate max-w-[260px]" title={r.resource_id || r.resource_name}>
                            {r.resource_id || r.resource_name || '—'}
                          </td>
                          <td className="p-2 text-gray-600">{r.region || 'global'}</td>
                          <td className="p-2">
                            <Badge variant="outline" className={severityBadge(r.severity)}>{(r.severity || '').toUpperCase()}</Badge>
                          </td>
                          <td className="p-2 text-gray-600">{r.status || 'open'}</td>
                        </tr>
                      ))}
                      {snapshot.affected_resources.length > 10 && (
                        <tr><td colSpan={4} className="p-2 text-center text-gray-500 italic">
                          …and {snapshot.affected_resources.length - 10} more — download the full snapshot for the complete list.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {snapshot.scan_id && (
              <p className="text-xs text-gray-500 font-mono">Source scan: {snapshot.scan_id}</p>
            )}
          </div>
        ) : data?.kind === 'file' ? (
          // ===== Regular uploaded file =====
          <div className="space-y-3" data-testid="evidence-file-view">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileText className="w-4 h-4" />
              <span className="font-mono">{data.file_name}</span>
              <span className="text-xs text-gray-400">·</span>
              <span className="text-xs uppercase">{data.file_type}</span>
              {data.file_size ? <><span className="text-xs text-gray-400">·</span><span className="text-xs">{(data.file_size / 1024).toFixed(1)} KB</span></> : null}
            </div>
            {inlineUrl ? (
              ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(data.file_type) ? (
                <img src={inlineUrl} alt={data.title} className="max-h-[60vh] mx-auto border border-gray-200 rounded" data-testid="evidence-image-preview" />
              ) : data.file_type === 'pdf' ? (
                <iframe src={inlineUrl} title="PDF Preview" className="w-full h-[60vh] border border-gray-200 rounded" data-testid="evidence-pdf-preview" />
              ) : null
            ) : (
              <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded border border-dashed border-gray-300 text-gray-500">
                <ImageIcon className="w-10 h-10 mb-2 text-gray-400" />
                <p className="text-sm">In-browser preview not supported for <code>.{data.file_type}</code>.</p>
                <p className="text-xs">Use Download to view the file locally.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-10 text-gray-500 text-sm" data-testid="evidence-empty-view">No file attached. Try Download for a generated snapshot.</div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
          <Button variant="outline" onClick={handleDownload} data-testid="evidence-preview-download">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="evidence-preview-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EvidencePreviewModal;
